<template>
  <form
    class="back-in-stock form-group"
    @submit.prevent="handleSubmit"
  >
    <div
      ref="bisWrap"
      className="form-group__field"
    >
      <label
        className="form-group__label text-desktop-14"
        htmlFor="form-group-text"
        v-text="buildInputLabel"
      />

      <input
        id="form-group-text"
        ref="bisInput"
        v-model="email"
        class="back-in-stock__input text-desktop-14"
        :class="{
          ['back-in-stock__input--success']: formState === 'success'
        }"
        type="email"
        required
      >
    </div>

    <btn
      type="submit"
      class="
          text-desktop-10
          back-in-stock__button
        "
      :class="{
        ['back-in-stock__button--disabled']: formState === 'submitting',
        ['back-in-stock__button--error']: formState === 'error',
        [$class('hidden')]: formState === 'success',
      }"
      block-class="product-form"
      :label="$string('product.back_in_stock.cta')"
      :show-text-icon="false"
    />

    <div
      v-if="formState != 'success'"
      class="checkbox back-in-stock__checkbox"
      :class="[
        $blockClass(blockClass, 'checkbox'),
      ]"
    >
      <input
        :id="`${layout}-back-in-stock-confirm`"
        class="checkbox__input"
        type="checkbox"
        required
      >
      <label
        class="checkbox__label"
        :htmlFor="`${layout}-back-in-stock-confirm`"
        v-text="$string('product.back_in_stock.confirm')"
      />
    </div>
  </form>
</template>

<script>

/**
 * Vue: Back in stock (back-in-stock)
 * -----------------------------------------------------------------------------
 * Snippet to handle adding a user to back-in-stock.
 *
 * @param {Boolean, Object} product - Shopify product object.
 * @param {Boolean, Object} variant - Shopify product variant object.
 */
import FormFields from '~/components/form-fields'
import Btn from '~global/btn/btn'

import { mapState } from 'vuex'

export default {
  name: 'BackInStock',

  components: {
    Btn,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },

    layout: {
      type: String,
      default: '',
    },

    product: {
      type: [Boolean, Object],
      default: false,
    },

    variant: {
      type: [Boolean, Object],
      default: false,
    },
  },

  data() {
    return {
      email: '',
      formState: 'pending',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Computes a label based on screen size.
     * @returns {String}
     */
    buildInputLabel() {
      return this.isMobile
        ? this.$string('product.back_in_stock.type_email')
        : this.$string('product.back_in_stock.email')
    },

    /**
     * Computes a label based on form state.
     * @returns {String}
     */
    buttonLabel() {
      return this.formState === 'submitting'
        ? this.$string('product.back_in_stock.sending')
        : this.$string('product.back_in_stock.send')
    },
  },

  mounted() {
    FormFields().init()
  },

  methods: {

    /**
     * Handles updating the messages displayed on adding to back in stock.
     * @param {String} message - Message to be displayed.
     * @param {String} state - Whether the message is a success or an error.
     */
    updateFormMessage(message, state) {
      const classUpdate = 'back-in-stock__message-display'
      const formDiv = this.$refs.bisWrap
      this.email = message

      formDiv.classList.add(classUpdate)
      if (state !== 'success') {
        this.formState = 'error'
        setTimeout(() => {
          formDiv.classList.remove(classUpdate)
          this.clearForm()
        }, this.$timing('message'))
        return
      }

      this.formState = 'success'
      setTimeout(() => {
        formDiv.classList.remove(classUpdate)
        this.clearForm()
      }, this.$timing('message'))
    },

    /**
     * Clears the form by resetting the initial data.
     */
    clearForm() {
      this.email = ''
      this.formState = 'pending'
      const input = this.$refs.bisInput

      FormFields().handleInputUpdate(
        {
          type: 'blur',
        },
        input,
      )
    },

    /**
     * Submits the form to BIS to register a new notification.
     */
    async handleSubmit() {
      if (this.formDisabled) {
        return
      }

      this.formState = 'submitting'
      const email = this.email
      const productId = this.product.id
      const variantId = this.variant.id

      try {
        /* eslint-disable no-new, no-undef */
        const bis = await BIS.create(email, variantId, productId)

        if (bis?.status === 'Error') {
          const message = bis.errors.base[0]
          this.updateFormMessage(message, 'error')
          this.handleGtm('error', message)
        } else if (bis?.status === 'OK') {
          const message = this.$string('product.back_in_stock.success')
          this.updateFormMessage(message, 'success')
          this.handleGtm('success', message)
        } else {
          const message = this.$string('product.back_in_stock.generic_error')
          this.updateFormMessage(message, 'error')
          this.handleGtm('error', message)
        }

      } catch (error) {
        const message = this.$string('product.back_in_stock.generic_error')
        this.updateFormMessage(message, 'error', true)
        this.handleGtm('error', message)
      }
    },

    handleGtm(status, message) {
      const dataLayer = window.dataLayer || []
      const url = document.location.pathname
      const product = this.product

      dataLayer.push({
        event: 'back_in_stock_submit',
        backInStockSubmit: {
          url,
          form_status: {
            status,
            message,
          },
          item: {
            handle: product.handle,
            id: product.id,
            price: product.price.amount,
            currency: product.price.currencyCode,
            variant_id: this.variant.id,
            variant_title: this.variant.title,
          },
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import './back-in-stock';
</style>
